import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { deleteDriver, DRIVERS_QUERY_KEY } from './DriverManager.service'
import type { DriverData } from './DriverManager.types'

interface DeleteDriverDialogPayload {
  driver: DriverData
}

interface DeleteDriverDialogProps {
  payload: DeleteDriverDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const DeleteDriverDialog = ({ payload, open, onAction, onClose }: DeleteDriverDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, formState, setError } = useForm()
  const queryClient = useQueryClient()
  const deleteDriverMutation = useMutation({
    mutationFn: deleteDriver,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DRIVERS_QUERY_KEY] }),
  })
  const [deletionConfirmed, setDeletionConfirmed] = useState(false)

  useEffect(() => {
    setDeletionConfirmed(false)
  }, [payload])

  const onSubmit: SubmitHandler<object> = async (): Promise<void> => {
    await handleFormSubmission(() => deleteDriverMutation.mutateAsync(payload.driver.cid), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction()
        analyticsEvent('polestar_cs_driver_deleted')
      },
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={`${payload.driver.name} — ${t('screens.cs.driver_manager.card.actions.delete')}`}
      actions={
        <Button
          type="submit"
          color="error"
          variant="contained"
          loading={formState.isSubmitting}
          disabled={!deletionConfirmed}
          onClick={handleSubmit(onSubmit)}
        >
          {t('screens.cs.driver_manager.delete_driver.delete')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
      maxWidth="xs"
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Typography>
        {t('screens.cs.driver_manager.delete_driver.confirmation')}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            value={deletionConfirmed}
            checked={deletionConfirmed}
            onChange={(): void => setDeletionConfirmed(!deletionConfirmed)}
          />
        }
        label={t('shared.manager.deletion_confirm_checkbox')}
      />
    </KNDialog>
  )
}

export default DeleteDriverDialog
